import React from 'react';
import { Stack } from 'expo-router';
import { IntlProvider } from 'react-intl';
import { Platform } from 'react-native';

import Head from 'expo-router/head';

import { useStripeDeepLink } from '@order/integrations-stripe';

import { App } from '../App';
import { ErrorBoundary, OrderStatusBar } from '../components';
import { DocumentTitle } from '../components/DocumentTitle';
import { NoticeBannersOverlay } from '../components/NoticeBannersOverlay';
import { useBrazeGenericDeepLinkingEvent, useRadarNotificationOpened } from '../context/Braze';
import { UrqlProvider } from '../context/Urql';
import { useInitialOrderPaymentState, useQuickActions, useTrackSignInSuccess } from '../hooks';
import { LaunchGate } from '../LaunchGate';
import { Providers } from '../Providers';
import {
  DEFAULT_SCREEN_OPTIONS,
  useCancelReorderOnModalExit,
  useCloseNoticeBannersOnModalExit,
  usePostLoginRedirect,
  useTabStackOptions,
  useTrackRoutes,
} from '../routing';

// ─────────────────────────────────────────────────────────────────────────────

export default function RootLayout() {
  return (
    <ErrorBoundary>
      <IntlProvider locale="en-US" defaultLocale="en-US">
        <UrqlProvider>
          <LaunchGate>
            <Providers>
              <App>
                <RouterStack />
              </App>
            </Providers>
          </LaunchGate>
        </UrqlProvider>
      </IntlProvider>
    </ErrorBoundary>
  );
}

// ─── Router Stack ────────────────────────────────────────────────────────────

const RouterStack = () => {
  const options = useTabStackOptions();

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackRoutes();
  useQuickActions();
  useCloseNoticeBannersOnModalExit();
  useCancelReorderOnModalExit();
  useInitialOrderPaymentState();
  useStripeDeepLink();
  useTrackSignInSuccess();
  usePostLoginRedirect();
  useRadarNotificationOpened();
  useBrazeGenericDeepLinkingEvent();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Head.Provider>
      <DocumentTitle />

      <Stack initialRouteName="(tabs)" screenOptions={SCREEN_OPTIONS}>
        <Stack.Screen name="(tabs)" options={options.Tabs} />
        <Stack.Screen name="(auth)" options={options.Auth} />
        <Stack.Screen name="(modals)" options={options.Modals} />
        <Stack.Screen name="(transparent-modals)" options={options.TransparentModals} />
        <Stack.Screen name="+not-found" options={options.NotFound} />
      </Stack>

      {/* NOTE: On iOS the status bar is controlled by React Navigation */}
      {Platform.OS === 'android' ? <OrderStatusBar /> : null}

      <NoticeBannersOverlay />
    </Head.Provider>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SCREEN_OPTIONS = {
  ...DEFAULT_SCREEN_OPTIONS,
  title: 'sweetgreen',
};

/**
 * Ensures Expo Router includes (tabs) in the navigation state when deep linking
 * to (modals) or (auth) on web. Without this, opening a modal URL directly only
 * renders the modal with no root page behind it.
 */
export const unstable_settings = {
  initialRouteName: '(tabs)',
};
